/**
 * Phase 3 (Feb 2026) — Campaign Wizard, 6 steps.
 *
 * "Eine Frage pro Bildschirm" als Gestaltungshilfe. Jeder Schritt ist
 * einzeln erreichbar, Zurück-Navigation verliert nichts (Persistenz
 * über /campaign-briefings/*), Abbrechen soft-archiviert den Draft.
 *
 * Steps:
 *   1. Ziel beschreiben                    → freier Text UND/ODER
 *                                              tenant-profil-abgeleitete Karten
 *   2. Fehlende Angaben ergänzen            → nur was aus dem Profil fehlt
 *   3. Strategie und Angebot prüfen         → Orchestrator-Draft mit
 *                                              object_version
 *   4. Budgetzuordnung und Befugnisse        → BudgetSlider + 3 Auto-Karten
 *   5. Vorschau                              → Creative + LP + optional
 *                                              Reichweiten-Schätzung
 *   6. Startauftrag bestätigen               → Aktionskarte wie Profi-Chat
 *
 * Cross-View: nutzt `useActiveBriefing()`; ändert Chat den Brief, sieht
 * der Wizard einen Konflikthinweis (dismissable / reloadbar) — die Backend-
 * If-Match-Regel erzwingt das serverseitig.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  Check, ChevronLeft, ChevronRight, Target, Compass, Sparkles,
  Wallet, Eye, ShieldCheck, X, RefreshCw, AlertTriangle,
} from "lucide-react";
import { api } from "../../lib/api";
import { useActiveBriefing } from "../../lib/campaign-briefing";
import BudgetSlider from "../../components/app/BudgetSlider";
import AggregationConsumedBadge from "../../components/app/AggregationConsumedBadge";

const STEP_META = [
  { key: "goal",     title: "Ziel beschreiben",             icon: Target },
  { key: "missing",  title: "Fehlende Angaben ergänzen",     icon: Compass },
  { key: "strategy", title: "Strategie & Angebot prüfen",    icon: Sparkles },
  { key: "budget",   title: "Budget & Befugnisse festlegen", icon: Wallet },
  { key: "preview",  title: "Vorschau",                      icon: Eye },
  { key: "confirm",  title: "Startauftrag bestätigen",       icon: ShieldCheck },
];

const AUTO_MODES = [
  {
    key: "prepare_only",
    title: "Nur vorbereiten",
    desc: "VIONA erstellt den Entwurf. Nichts wird veröffentlicht — du triggerst jeden Schritt manuell.",
  },
  {
    key: "with_approval",
    title: "Mit meiner Freigabe",
    desc: "VIONA schlägt Aktionen vor. Jede geht durch deine Freigabe, bevor sie live läuft.",
  },
  {
    key: "auto_within_frame",
    title: "Automatisch in meinem Rahmen",
    desc: "VIONA handelt innerhalb der von dir gesetzten Grenzen (Budget-Cap, Kanäle, Zielgruppen). Du bleibst Owner, Audit läuft mit.",
  },
];

// ── Component ──────────────────────────────────────────────────────

export default function CampaignWizard() {
  const [tenant, setTenant] = useState(null);
  const { briefing, loading, save, abandon, refresh, conflict, dismissConflict } =
    useActiveBriefing();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  const step = Math.min(
    Math.max(parseInt(sp.get("step") || briefing?.current_step || "1", 10), 1),
    6,
  );

  useEffect(() => {
    api
      .get("/tenants/me")
      .then((r) => setTenant(r.data || null))
      .catch(() => setTenant(null));
  }, []);

  useEffect(() => {
    if (briefing && !sp.get("step")) {
      setSp({ step: String(briefing.current_step || 1) }, { replace: true });
    }
  }, [briefing]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !briefing) {
    return (
      <Card data-testid="wizard-loading">
        <CardContent className="py-8 text-sm text-muted-foreground text-center">
          Lade Briefing …
        </CardContent>
      </Card>
    );
  }

  async function goToStep(nextStep, patch) {
    const nextSteps = { ...(briefing.steps || {}), ...(patch || {}) };
    await save({
      steps: nextSteps,
      current_step: nextStep,
      origin: "wizard",
      status: "draft",
    });
    setSp({ step: String(nextStep) }, { replace: false });
  }

  async function cancelDraft() {
    await abandon();
    toast("Entwurf archiviert. Kein Kostenimpakt.");
    navigate("/app/home");
  }

  const CurrentIcon = STEP_META[step - 1].icon;

  return (
    <div data-testid="campaign-wizard" data-step={step} className="space-y-6">
      <header className="space-y-1">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <CurrentIcon className="w-4 h-4" />
          Schritt {step} von 6
          <span className="ml-2">·</span>
          <Badge variant="outline" data-testid="wizard-object-version">
            object_version&nbsp;{briefing.object_version}
          </Badge>
          <span className="ml-auto">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/app/home?open-chat=1" data-testid="wizard-switch-to-chat">
                Zum Profi-Chat
              </Link>
            </Button>
            <Button variant="ghost" size="sm" onClick={cancelDraft}
                     data-testid="wizard-abandon">
              <X className="w-3 h-3 mr-1" />
              Abbrechen
            </Button>
          </span>
        </div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
          {STEP_META[step - 1].title}
        </h1>
      </header>

      {conflict && (
        <Alert
          data-testid="wizard-conflict-banner"
          className="border-amber-500"
        >
          <AlertTitle className="text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            {conflict.last_source === "chat"
              ? "Der Chat hat den Briefing-Entwurf geändert"
              : "Der Wizard-Entwurf wurde extern geändert"}
          </AlertTitle>
          <AlertDescription className="text-xs flex items-center gap-2">
            Aktuelle Version: {conflict.current_version} — deine Version war {conflict.expected}.
            <Button size="sm" variant="outline" onClick={() => { refresh(); dismissConflict(); }}
                     data-testid="wizard-conflict-reload">
              <RefreshCw className="w-3 h-3 mr-1" />
              Neu laden
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Step navigator (all steps clickable, no data loss) */}
      <nav className="flex flex-wrap gap-1" data-testid="wizard-steps-nav">
        {STEP_META.map((m, i) => {
          const idx = i + 1;
          return (
            <button
              key={m.key}
              onClick={() => setSp({ step: String(idx) })}
              data-testid={`wizard-step-nav-${idx}`}
              data-active={idx === step ? "true" : "false"}
              className={
                "px-2 py-1 rounded text-xs " +
                (idx === step
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-accent")
              }
            >
              {idx}. {m.title}
            </button>
          );
        })}
      </nav>

      <div data-testid={`wizard-step-panel-${step}`}>
        {step === 1 && <StepGoal briefing={briefing} tenant={tenant} onNext={(patch) => goToStep(2, patch)} />}
        {step === 2 && <StepMissing briefing={briefing} tenant={tenant}
                                     onBack={() => setSp({ step: "1" })}
                                     onNext={(patch) => goToStep(3, patch)} />}
        {step === 3 && <StepStrategy briefing={briefing}
                                       onBack={() => setSp({ step: "2" })}
                                       onNext={(patch) => goToStep(4, patch)} />}
        {step === 4 && <StepBudget briefing={briefing}
                                     onBack={() => setSp({ step: "3" })}
                                     onNext={(patch) => goToStep(5, patch)} />}
        {step === 5 && <StepPreview briefing={briefing}
                                     onBack={() => setSp({ step: "4" })}
                                     onNext={(patch) => goToStep(6, patch)} />}
        {step === 6 && <StepConfirm briefing={briefing}
                                     onBack={() => setSp({ step: "5" })} />}
      </div>
    </div>
  );
}

// ── Step 1 — Ziel beschreiben ──────────────────────────────────────

function StepGoal({ briefing, tenant, onNext }) {
  const [goal, setGoal] = useState(briefing.steps?.goal || "");
  const cards = useMemo(() => {
    // Cards derived from CONFIRMED tenant profile. No hardcoded
    // Wuppertal/Immobilien defaults for foreign tenants.
    if (!tenant) return [];
    const industry = tenant.industry || tenant.category;
    const regions = tenant.regions || [];
    const products = tenant.products || [];
    const out = [];
    if (regions.length && products.length) {
      products.slice(0, 3).forEach((p) => {
        regions.slice(0, 2).forEach((r) => {
          out.push({
            id: `${p}::${r}`,
            title: `Mehr Anfragen für ${p} in ${r}`,
            subtitle: industry ? `Branche: ${industry}` : "",
          });
        });
      });
    } else if (regions.length) {
      regions.slice(0, 3).forEach((r) =>
        out.push({ id: `region:${r}`,
                   title: `Sichtbarer werden in ${r}`,
                   subtitle: industry ? `Branche: ${industry}` : "" }),
      );
    } else if (products.length) {
      products.slice(0, 3).forEach((p) =>
        out.push({ id: `product:${p}`, title: `${p} bekannter machen` }),
      );
    }
    return out;
  }, [tenant]);

  return (
    <Card data-testid="wizard-step-1">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Was möchtest du erreichen?</CardTitle>
        <p className="text-xs text-muted-foreground">
          Beschreibe es in deinen Worten. VIONA übersetzt den Wunsch später in
          eine Kampagnen-Strategie.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Textarea
          data-testid="wizard-goal-input"
          rows={3}
          placeholder="z. B. mehr passende Anfragen aus meiner Region"
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
        />
        {cards.length > 0 && (
          <div className="space-y-2" data-testid="wizard-goal-cards">
            <div className="text-xs text-muted-foreground">
              Vorschläge basierend auf deinem Firmenprofil:
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {cards.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setGoal(c.title)}
                  data-testid={`wizard-goal-card-${c.id}`}
                  className="text-left rounded border p-2 text-sm hover:bg-accent transition-colors"
                >
                  <div className="font-medium">{c.title}</div>
                  {c.subtitle && (
                    <div className="text-xs text-muted-foreground">{c.subtitle}</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <Button
            disabled={!goal.trim()}
            data-testid="wizard-next-1"
            onClick={() => onNext({ goal, goal_source: goal ? "text" : null })}
          >
            Weiter <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Step 2 — Fehlende Angaben ─────────────────────────────────────

function StepMissing({ briefing, tenant, onBack, onNext }) {
  const [answers, setAnswers] = useState(briefing.steps?.missing_answers || {});
  // Only ask questions that the tenant profile can't already answer.
  const asks = [];
  if (!tenant?.regions || tenant.regions.length === 0) {
    asks.push({ key: "regions", label: "In welchen Regionen möchtest du werben?",
                 placeholder: "z. B. Berlin, Hamburg, München" });
  }
  if (!tenant?.products || tenant.products.length === 0) {
    asks.push({ key: "offer", label: "Was genau bietest du an?",
                 placeholder: "kurz und in deinen Worten" });
  }
  if (!tenant?.website) {
    asks.push({ key: "website", label: "Website oder Landingpage",
                 placeholder: "https://…" });
  }
  return (
    <Card data-testid="wizard-step-2">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Fehlende Angaben ergänzen</CardTitle>
        <p className="text-xs text-muted-foreground">
          Bekannte Werte aus deinem Profil sind schon eingesetzt.
          Nur was fehlt, wird hier gefragt.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {asks.length === 0 && (
          <div className="rounded border p-3 text-sm bg-muted/40"
               data-testid="wizard-missing-none">
            Alle Angaben aus deinem Profil vorhanden. Weiter zum Strategie-Entwurf.
          </div>
        )}
        {asks.map((a) => (
          <label key={a.key} className="block text-sm space-y-1">
            <span>{a.label}</span>
            <Input
              placeholder={a.placeholder}
              data-testid={`wizard-missing-${a.key}`}
              value={answers[a.key] || ""}
              onChange={(e) =>
                setAnswers((v) => ({ ...v, [a.key]: e.target.value }))
              }
            />
          </label>
        ))}
        <div className="flex justify-between pt-2">
          <Button variant="ghost" onClick={onBack} data-testid="wizard-back-2">
            <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          <Button
            data-testid="wizard-next-2"
            onClick={() => onNext({ missing_answers: answers })}
          >
            Weiter <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Step 3 — Strategie & Angebot ──────────────────────────────────

function StepStrategy({ briefing, onBack, onNext }) {
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(briefing.steps?.strategy || null);
  async function generate() {
    setBusy(true);
    try {
      const r = await api.post("/ai/campaign-brief", {
        goal: briefing.steps?.goal || "",
        locale: "de",
        explicit_overrides: briefing.steps?.missing_answers || {},
      });
      setProposal(r.data?.proposal || r.data || {});
    } catch (e) {
      toast.error("Konnte keinen Vorschlag erzeugen");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card data-testid="wizard-step-3">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Strategie und Angebot prüfen</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!proposal && (
          <Button onClick={generate} disabled={busy}
                   data-testid="wizard-generate-strategy">
            {busy ? "…" : "Vorschlag erzeugen"}
          </Button>
        )}
        {proposal && (
          <div className="rounded border p-3 text-sm space-y-2"
               data-testid="wizard-strategy-preview">
            <div className="font-medium">
              {proposal.headline || proposal.name || "Kampagnen-Entwurf"}
            </div>
            <div className="text-xs text-muted-foreground whitespace-pre-wrap">
              {proposal.summary || proposal.description ||
               "Vorschlag geladen — Details im nächsten Schritt."}
            </div>
          </div>
        )}
        <div className="flex justify-between pt-2">
          <Button variant="ghost" onClick={onBack} data-testid="wizard-back-3">
            <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          <Button
            data-testid="wizard-next-3"
            disabled={!proposal}
            onClick={() => onNext({ strategy: proposal })}
          >
            Weiter <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Step 4 — Budget & Befugnisse ──────────────────────────────────

function StepBudget({ briefing, onBack, onNext }) {
  const [budget_cents, setBudgetCents] = useState(
    briefing.steps?.budget?.daily_cents ?? 0,
  );
  const [auto, setAuto] = useState(
    briefing.steps?.auto_mode || "with_approval",
  );
  return (
    <Card data-testid="wizard-step-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          Budgetzuordnung und Befugnisse festlegen
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <BudgetSlider
          value_cents={budget_cents}
          onChange={setBudgetCents}
          target_leads_per_month={30}
          category={briefing.steps?.strategy?.category || null}
        />

        <AggregationConsumedBadge variant="block" refreshKey={budget_cents} />

        <div className="space-y-2" data-testid="wizard-auto-modes">
          <div className="text-sm font-medium">
            Wie viel Autonomie gibst du VIONA?
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {AUTO_MODES.map((m) => (
              <button
                key={m.key}
                onClick={() => setAuto(m.key)}
                data-testid={`wizard-auto-mode-${m.key}`}
                data-active={auto === m.key ? "true" : "false"}
                className={
                  "text-left rounded border p-3 text-sm transition-colors " +
                  (auto === m.key
                    ? "border-primary bg-primary/5"
                    : "border-muted hover:bg-accent")
                }
              >
                <div className="flex items-center gap-2 font-medium">
                  {auto === m.key && <Check className="w-4 h-4 text-primary" />}
                  {m.title}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {m.desc}
                </div>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Keine „volle Freiheit" ohne Rahmen — du bleibst immer Owner.
          </p>
        </div>

        <div className="flex justify-between pt-2">
          <Button variant="ghost" onClick={onBack} data-testid="wizard-back-4">
            <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          <Button
            data-testid="wizard-next-4"
            onClick={() => onNext({
              budget: { daily_cents: budget_cents },
              auto_mode: auto,
            })}
          >
            Weiter <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Step 5 — Preview ──────────────────────────────────────────────

function StepPreview({ briefing, onBack, onNext }) {
  // Reach estimate only if we have a real basis; otherwise omit.
  const hasBasis = false; // Set to true when a provider forecast is wired.
  return (
    <Card data-testid="wizard-step-5">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Vorschau</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded border p-3 text-sm"
             data-testid="wizard-preview-creative">
          <div className="text-xs text-muted-foreground mb-1">
            Anzeigen-Preview
          </div>
          <div className="whitespace-pre-wrap">
            {briefing.steps?.strategy?.headline || "Kein Preview verfügbar"}
          </div>
        </div>
        {!hasBasis && (
          <div
            className="text-xs rounded border p-2 bg-muted/40"
            data-testid="wizard-preview-no-forecast"
          >
            Keine belastbare Reichweiten-Schätzung verfügbar — wir zeigen
            keine Zahl ohne Quelle. Nach dem Start liefert der Anbieter
            echte Zahlen; die erscheinen dann automatisch in der Übersicht.
          </div>
        )}
        <div className="flex justify-between pt-2">
          <Button variant="ghost" onClick={onBack} data-testid="wizard-back-5">
            <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          <Button
            data-testid="wizard-next-5"
            onClick={() => onNext({ preview_seen: true })}
          >
            Weiter <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Step 6 — Confirm ──────────────────────────────────────────────

function StepConfirm({ briefing, onBack }) {
  const [preparing, setPreparing] = useState(false);
  const [card, setCard] = useState(null);
  async function prepareApproval() {
    setPreparing(true);
    try {
      const r = await api.post("/profi-chat/message", {
        content: `Kampagne live schalten laut Briefing ${briefing.id}`,
        locale: "de",
      });
      setCard(r.data?.action_card || null);
    } catch (e) {
      toast.error("Vorbereitung fehlgeschlagen");
    } finally {
      setPreparing(false);
    }
  }
  const blockers = [];
  if (!briefing.steps?.goal) blockers.push("Ziel fehlt (Schritt 1).");
  if (!briefing.steps?.strategy) blockers.push("Strategie fehlt (Schritt 3).");
  if (!briefing.steps?.budget?.daily_cents) {
    blockers.push("Budgetzuordnung fehlt (Schritt 4).");
  }
  return (
    <Card data-testid="wizard-step-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Startauftrag bestätigen</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {blockers.length > 0 ? (
          <div className="rounded border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/20 p-3 text-sm space-y-1"
               data-testid="wizard-confirm-blockers">
            <div className="font-medium">Voraussetzungen abschließen</div>
            <ul className="list-disc list-inside text-xs">
              {blockers.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </div>
        ) : !card ? (
          <Button onClick={prepareApproval} disabled={preparing}
                   data-testid="wizard-confirm-prepare">
            {preparing ? "…" : "Freigabe-Vorschlag erzeugen"}
          </Button>
        ) : (
          <div className="rounded border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 text-sm space-y-2"
               data-testid="wizard-confirm-action-card">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Klasse&nbsp;C</Badge>
              <span className="font-medium">{card.title}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <Meta label="Objekt-Version" value={card.object_version} testid="wc-obj-version" />
              <Meta label="Bindung-Hash" value={(card.param_binding_hash_prefix || "") + "…"} testid="wc-binding-hash" />
              <Meta label="Aktor" value={card.actor} testid="wc-actor" />
              <Meta label="Kosten"
                     value={card.cost_estimate_eur != null ? `${card.cost_estimate_eur} €` : "—"}
                     testid="wc-cost" />
            </div>
            <div className="text-[11px] text-muted-foreground">
              Ausführung passiert NICHT automatisch nach Freigabe. Sie ist ein
              expliziter, separater Trigger — auch dann ohne „garantiert" oder
              feste Wartezeit-Zusagen.
            </div>
          </div>
        )}
        <div className="flex justify-between pt-2">
          <Button variant="ghost" onClick={onBack} data-testid="wizard-back-6">
            <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Meta({ label, value, testid }) {
  return (
    <div className="rounded border bg-background/70 px-2 py-1 flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono text-[11px]" data-testid={testid}>
        {value ?? "—"}
      </span>
    </div>
  );
}
